import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../apiClient';
import { getAllSessions as getLocalSessions, getPendingCountsBySession } from '../../services/localRecording/db';
import { startUploader, wakeUploader, subscribeUploader } from '../../services/localRecording/uploader';
import './MySessions.css';

/**
 * MySessions
 *
 * Visar en lista över alla sessioner för inloggad användare.
 * Tillåter radering av session samt navigering till MainContent
 * för att återuppta (paused) eller visa journal (completed).
 */
function MySessions() {
  const [sessions, setSessions] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCompleteAllModal, setShowCompleteAllModal] = useState(false);
  const [completingAll, setCompletingAll] = useState(false);
  // Lokal-först: lokala (ännu ej bekräftade) chunkar + rent lokala sessioner.
  const [localPending, setLocalPending] = useState({});   // { client_session_id: {pending, permanent_failure} }
  const [localSessions, setLocalSessions] = useState([]);  // IndexedDB-sessioner

  const navigate = useNavigate();

  const refreshLocal = useCallback(async () => {
    try {
      const [counts, sess] = await Promise.all([
        getPendingCountsBySession(),
        getLocalSessions(),
      ]);
      setLocalPending(counts);
      setLocalSessions(sess || []);
    } catch (_) { /* IndexedDB kan saknas */ }
  }, []);

  useEffect(() => {
    fetchSessions();
    // Se till att uppladdaren snurrar och håll lokal-status färsk medan vyn är öppen.
    startUploader();
    refreshLocal();
    const unsub = subscribeUploader((ev) => {
      if (ev.type === 'pending_changed' || ev.type === 'session_completed') {
        refreshLocal();
        if (ev.type === 'session_completed') fetchSessions();
      }
    });
    const iv = setInterval(refreshLocal, 4000);
    return () => { unsub(); clearInterval(iv); };
  }, [refreshLocal]);

  const fetchSessions = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await apiClient.get('/recorder/sessions/my_sessions');
      if (Array.isArray(resp.data)) {
        setSessions(resp.data);
      } else {
        setError('Felaktigt svar från servern.');
      }
    } catch (err) {
      console.error('Fel vid hämtning av sessions:', err);
      setError('Kunde inte hämta sessions.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * handleResume(session)
   * Om status är "paused", navigera till /main-content med session_id,
   * så att man kan återuppta inspelningen.
   * Om status är "completed", navigera till /main-content med session_id,
   * men du kan ex. skicka en query-param "completed=1" om du vill öppna journalen direkt.
   */
  const handleOpenSession = (session) => {
    if (session.status === 'diktering') {
      // Navigate to SmartDictation for diktering sessions
      navigate(`/smart-dictation?session_id=${session.session_id}`);
    } else if (session.status === 'paused' || session.status === 'in_progress') {
      // Navigate to MainContent for regular recording sessions
      navigate(`/main-content?session_id=${session.session_id}`);
    } else if (session.status === 'completed') {
      // Navigate to MainContent for completed sessions
      navigate(`/main-content?session_id=${session.session_id}&completed=1`);
    } else {
      alert(`Okänt status: ${session.status}`);
    }
  };

  /**
   * handleDeleteSession(session_id)
   * Klarmarkerar sessionen (arkiverar den permanent).
   */
  const handleDeleteSession = async (session_id) => {
    // Skapa en custom modal istället för window.confirm
    const confirmDelete = () => {
      return new Promise((resolve) => {
        const modal = document.createElement('div');
        modal.className = 'confirm-modal-overlay';
        modal.innerHTML = `
          <div class="confirm-modal">
            <h3>Klarmarkera journal</h3>
            <p>Vill du klarmarkera denna journal?</p>
            <p class="warning-text">Detta arkiverar journalen permanent och den kan inte återställas.</p>
            <div class="modal-buttons">
              <button class="modal-button cancel-button" onclick="this.closest('.confirm-modal-overlay').remove(); window.resolveConfirm(false)">Avbryt</button>
              <button class="modal-button confirm-button" onclick="this.closest('.confirm-modal-overlay').remove(); window.resolveConfirm(true)">Klarmarkera</button>
            </div>
          </div>
        `;
        document.body.appendChild(modal);
        window.resolveConfirm = resolve;
      });
    };
    
    const confirmed = await confirmDelete();
    if (!confirmed) {
      return;
    }
    try {
      // OBS! Du måste skapa en DELETE-endpoint i backend, t.ex.:
      // DELETE /recorder/sessions/<session_id>
      await apiClient.delete(`/recorder/sessions/${session_id}`);
      // Ta bort ur listan i state:
      setSessions((prev) => prev.filter((s) => s.session_id !== session_id));
    } catch (err) {
      console.error('Fel vid radering av session:', err);
      alert('Kunde inte radera session.');
    }
  };

  /**
   * handleCompleteAll()
   * Klarmarkerar (arkiverar) samtliga sessioner på en gång. Kan inte ångras.
   */
  const handleCompleteAll = async () => {
    setCompletingAll(true);
    try {
      await apiClient.post('/recorder/sessions/complete_all');
      setSessions([]);
      setShowCompleteAllModal(false);
    } catch (err) {
      console.error('Fel vid klarmarkering av alla sessioner:', err);
      alert('Kunde inte klarmarkera alla sessioner.');
    } finally {
      setCompletingAll(false);
    }
  };

  // Sätt statustext
  const translateStatus = (status) => {
    switch (status) {
      case 'in_progress':
        return 'Pågående';
      case 'paused':
        return 'Pausad';
      case 'completed':
        return 'Avslutad';
      case 'diktering':
        return 'Diktering';
      default:
        return status;
    }
  };

  const handleRetryUpload = () => {
    wakeUploader();
    setTimeout(refreshLocal, 500);
  };

  // Leverans-badge (lokal-först): visar om en session inte är fullständigt skickad
  // eller om något gått permanent fel. Returnerar JSX eller null.
  const deliveryBadge = (session) => {
    const local = session.client_session_id ? localPending[session.client_session_id] : null;
    if (local?.pending > 0) {
      return (
        <span className="delivery-badge sending" title="Delar av inspelningen ligger sparade lokalt och skickas till servern. Stäng inte webbläsaren förrän det är klart.">
          <i className="fas fa-cloud-upload-alt"></i> Skickas ({local.pending})
        </span>
      );
    }
    if (local?.permanent_failure > 0) {
      return (
        <span className="delivery-badge failed" title="Delar kunde inte skickas trots många försök. Klicka 'Skicka igen' eller kontakta support.">
          <i className="fas fa-exclamation-triangle"></i> Ej skickad ({local.permanent_failure})
        </span>
      );
    }
    if (session.has_permanent_failure) {
      return (
        <span className="delivery-badge failed" title={`${session.permanent_failed_chunks || ''} del(ar) kunde inte transkriberas. Journalen kan sakna delar av mötet.`}>
          <i className="fas fa-exclamation-triangle"></i> Delar saknas
        </span>
      );
    }
    if (session.processing_status === 'has_pending_retries' || (session.pending_chunks_count || 0) > 0) {
      return (
        <span className="delivery-badge sending" title="Delar av inspelningen bearbetas fortfarande i bakgrunden.">
          <i className="fas fa-sync fa-spin"></i> Bearbetar
        </span>
      );
    }
    return null;
  };

  // Rent lokala sessioner: inspelade men saknar server-rad (t.ex. servern var
  // nere vid start). Visas separat så användaren ser att de väntar på att skickas.
  const serverClientIds = new Set(sessions.map((s) => s.client_session_id).filter(Boolean));
  const orphanLocal = localSessions.filter((ls) => {
    if (serverClientIds.has(ls.client_session_id)) return false;
    const c = localPending[ls.client_session_id];
    return c && (c.pending > 0 || c.permanent_failure > 0);
  });

  return (
    <div className="my-sessions-container">
      <h2>Mina patientmöten</h2>

      {!loading && sessions.length > 0 && (
        <div className="complete-all-bar">
          <button
            className="complete-all-button"
            onClick={() => setShowCompleteAllModal(true)}
          >
            Klarmarkera alla
          </button>
        </div>
      )}

      {showCompleteAllModal && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal">
            <h3>Klarmarkera alla sessioner</h3>
            <p>Nu klarmarkerar du samtliga sessioner.</p>
            <p className="warning-text">
              Detta arkiverar alla journaler permanent och kan inte ångras.
            </p>
            <div className="modal-buttons">
              <button
                className="modal-button cancel-button"
                onClick={() => setShowCompleteAllModal(false)}
                disabled={completingAll}
              >
                Avbryt
              </button>
              <button
                className="modal-button confirm-button"
                onClick={handleCompleteAll}
                disabled={completingAll}
              >
                {completingAll ? 'Klarmarkerar...' : 'Klarmarkera alla'}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && <p>Laddar...</p>}
      {error && <p className="error-message">{error}</p>}

      {orphanLocal.length > 0 && (
        <div className="local-unsent-banner">
          <div className="local-unsent-text">
            <i className="fas fa-cloud-upload-alt"></i>{' '}
            {orphanLocal.length === 1 ? 'En inspelning' : `${orphanLocal.length} inspelningar`}{' '}
            är sparade lokalt men har ännu inte kunnat skickas till servern
            (t.ex. om servern var nere). De skickas automatiskt — stäng inte
            webbläsaren helt förrän de är levererade.
          </div>
          <button className="retry-upload-button" onClick={handleRetryUpload}>
            <i className="fas fa-sync"></i> Skicka igen nu
          </button>
        </div>
      )}

      {!loading && sessions.length === 0 && orphanLocal.length === 0 && (
        <p>Inga sessioner att visa.</p>
      )}

      {!loading && sessions.length > 0 && (
        <table className="sessions-table">
          <thead>
            <tr>
              <th>Skapad</th>
              <th>Status</th>
              <th>Kontaktorsak</th>
              <th>Journalmall</th>
              <th>Åtgärder</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((session) => (
              <tr key={session.session_id}>
                <td>
                  {session.created_at
                    ? new Date(session.created_at).toLocaleString('sv-SE')
                    : 'Okänd'}
                </td>
                <td>
                  <span className={`status-badge status-${session.status}`}>
                    {translateStatus(session.status)}
                  </span>
                  {deliveryBadge(session)}
                </td>
                <td>
                  {session.is_purged ? (
                    <span className="purge-badge purged" title="Transkript och genererat innehåll har gallrats enligt klinikens lagringstid. Journal sparad i journalsystemet påverkas inte.">
                      <i className="fas fa-eraser"></i> Gallrad
                    </span>
                  ) : (
                    <>
                      {session.session_name || session.summary || session.transcription_text}
                      {session.days_until_purge !== undefined && session.days_until_purge <= 7 && (
                        <span className="purge-badge warning" title={`Transkriptet gallras ${session.purge_at ? new Date(session.purge_at).toLocaleDateString('sv-SE') : 'snart'}. Spara journalen till journalsystemet innan dess.`}>
                          <i className="fas fa-clock"></i> Gallras om {session.days_until_purge} {session.days_until_purge === 1 ? 'dag' : 'dagar'}
                        </span>
                      )}
                    </>
                  )}
                </td>
                <td>
                  {session.template_name ? 
                    String(session.template_name) : 
                    (session.status === 'in_progress' ? 'Pågående' : 
                     session.status === 'paused' ? 'Pausad' : 
                     'Ej vald')
                  }
                </td>
                <td className="action-buttons">
                  <button
                    className="session-button open-session-button"
                    onClick={() => handleOpenSession(session)}
                  >
                    {session.status === 'completed'
                      ? 'Visa journal'
                      : session.status === 'diktering'
                      ? 'Fortsätt diktering'
                      : 'Återuppta'}
                  </button>
                  <button
                    className="session-button delete-session-button"
                    onClick={() => handleDeleteSession(session.session_id)}
                  >
                    Klarmarkera
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default MySessions;
